import Emmiter from 'tiny-emitter';
import { v4 as uuid } from 'uuid';
import UploadScheduler from './upload-scheduler';

const DEFAULT_MAX_UPLOAD_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY = 500;

const eventTypes = {
  UPLOAD_PROGRESS: (id) => `upload_progress:${id}`,
  UPLOAD_ERROR: (id) => `upload_error:${id}`,
  UPLOAD_COMPLETE: (id) => `upload_complete:${id}`,
};

class ImageUploader {
  constructor(options = {}) {
    this._uploadScheduler = new UploadScheduler({
      concurrentUploads: options.concurrentUploads || options.threads,
      maxConnections: options.maxConnections || options.threads,
    });
    this.maxUploadAttempts = options.maxUploadAttempts || DEFAULT_MAX_UPLOAD_ATTEMPTS;
    this.retryDelay = options.retryDelay || DEFAULT_RETRY_DELAY;
  }

  debug = false;

  _emmiter = new Emmiter();

  // Add
  enqueue = (file, options) => {
    if (!file.id) file.id = uuid();
    if (options) {
      const { onProgress, onError, onComplete } = options;
      if (typeof onProgress === 'function') {
        this._emmiter.on(eventTypes.UPLOAD_PROGRESS(file.id), onProgress);
      }
      if (typeof onError === 'function') {
        this._emmiter.on(eventTypes.UPLOAD_ERROR(file.id), onError);
      }
      if (typeof onComplete === 'function') {
        this._emmiter.on(eventTypes.UPLOAD_COMPLETE(file.id), onComplete);
      }
    }
    this._startUpload(file);
    return file.id;
  };

  _startUpload = (file, attempt = 1) => {
    this._uploadScheduler
      .enqueue(file.id, () => {
        this.debug && console.log('[ImageUploader] queue is ready. starting next upload');
        return this.upload(file);
      })
      .catch((error) => {
        if (attempt < this.maxUploadAttempts && this._isRetryableUploadError(error)) {
          this._scheduleRetry(file, attempt);
          return;
        }
        this._handleUploadError(file, error);
      });
  };

  _scheduleRetry = async (file, attempt) => {
    await this._waitForOnline();
    const retryDelay = this.retryDelay * 2 ** (attempt - 1);
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    await this._waitForOnline();
    this._emitProgress(file.id, { progress: 0, file });
    this._startUpload(file, attempt + 1);
  };

  _waitForOnline = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || navigator.onLine) {
      return Promise.resolve();
    }
    return new Promise((resolve) => window.addEventListener('online', resolve, { once: true }));
  };

  _isRetryableUploadError = (error) => {
    const status = error?.response?.status;
    return !status || status === 408 || status === 429 || status >= 500;
  };

  _handleUploadError = (file, error) => {
    this._emitError(file.id, { error, file });
  };

  _emitProgress = (id, { progress, uploadComplete = progress >= 100, ...payload }) => {
    if (uploadComplete) this._uploadScheduler.markUploadComplete(id);
    this._emmiter.emit(eventTypes.UPLOAD_PROGRESS(id), { progress, ...payload });
  };

  _emitError = (id, { error, ...payload }) => {
    this._emmiter.emit(eventTypes.UPLOAD_ERROR(id), { error, ...payload });
  };

  _emitComplete = (id, { url, ...payload }) => {
    this._emmiter.emit(eventTypes.UPLOAD_COMPLETE(id), { url, ...payload });
  };

  // Upload simulation
  // It should be overriden!
  upload = (file) =>
    new Promise((resolve) => {
      this.debug && console.log('[ImageUploader] started upload simulation', file);
      setTimeout(() => {
        this.debug && console.log('[ImageUploader] finished upload simulation', file);
        resolve(file);
      }, 5000);
    });
}

export default ImageUploader;

// const iu = new ImageUploader();
// [...new Array(15)].forEach((asdf, i) => iu.enqueue(i + 1));
